import styled from "@emotion/styled/macro";
import {
  Button,
  Typography,
  LoadingIndicator,
} from "@sunrun/experience-ui-components";
import { ButtonProps } from "@sunrun/experience-ui-components/lib/components/Button/Button";
import { ReactNode, type Dispatch, type SetStateAction } from "react";
import Resizer from "react-image-file-resizer";
import { LoadingIndicatorProps } from "@sunrun/experience-ui-components/lib/components/LoadingIndicator/LoadingIndicator";
import { useMediaQuery } from "react-responsive";
import { SplatCheckoutDocument } from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { AttachmentActionButton } from "components/atoms/AttachmentActionButton";
import { Attachment, defaultAllowedFileTypes } from "utils/usageUtils";

enum ErrorType {
  "UPLOAD_FAILED" = 1,
}

type FileUploadError = {
  fileName: string;
  errorType: ErrorType;
};

const IMAGE_RESIZE_THRESHOLD_MB = 4.7;

const resizeFile = (file: File) => {
  return new Promise<File>((resolve) => {
    Resizer.imageFileResizer(
      file,
      3000,
      3000,
      "JPEG",
      80,
      0,
      (uri) => {
        resolve(uri as File);
      },
      "file"
    );
  });
};

const isImageFile = (file: File) => {
  return file.type.search(/(png|jpeg|jpg|webp)$/i) !== -1;
};

const shouldResizeImage = (file: File) => {
  return (
    isImageFile(file) &&
    file.size > IMAGE_RESIZE_THRESHOLD_MB * Math.pow(1024, 2)
  );
};

const handleFileSelected = async (
  fileListObject: FileList,
  {
    allowedFileTypes = defaultAllowedFileTypes,
    documentType,
    onUploadedNewFiles,
    setIsDirty = () => {},
  }: {
    allowedFileTypes?: string[];
    documentType: string;
    onError?: (errors: FileUploadError[]) => void;
    onUploadedNewFiles: Dispatch<SetStateAction<Attachment[]>>;
    setIsDirty?: (arg0: boolean) => void;
  }
) => {
  const filesArray = Array.from(fileListObject);
  if (!filesArray || filesArray.length === 0) return;

  let hasFormattedFiles = false;

  const returnArrPromise = filesArray.reduce((newFilesArr, file: File) => {
    if (!allowedFileTypes.includes(file.type)) {
      return newFilesArr;
    }

    if (shouldResizeImage(file)) {
      hasFormattedFiles = true;
      return newFilesArr.concat(resizeFile(file));
    }

    return newFilesArr.concat(file);
  }, [] as Array<File | Promise<File>>);

  const newFilesArr = hasFormattedFiles
    ? await Promise.all(returnArrPromise)
    : (returnArrPromise as Array<File>);

  const formattedFileData = newFilesArr.map((file) => ({
    documentName: file.name,
    documentType: documentType,
    url: URL.createObjectURL(file),
    contentType: file.type,
    documentBlob: file,
    new: true,
  }));

  onUploadedNewFiles((prev) => [...prev, ...formattedFileData]);
  setIsDirty(true);
};

const UploadAttachments = ({
  setIsDirty = () => {},
  uploadedFiles,
  uploadedNewFiles,
  onUploadedNewFiles,
  onError,
  description,
  documentType,
  allowedFileTypes = defaultAllowedFileTypes,
  allowDownload = false,
  allowPreview = false,
  acceptMultipleFiles = true,
  buttonSize = "lg",
  disabled = false,
  documentLoading,
  loadingColor = "white",
  buttonText = "Upload",
  buttonIcon,
  buttonColor,
  onButtonClick,
  getBillOnline,
  intervalDataStatus,
}: UploadAttachmentsProps) => {
  const uploadId = documentType.toLowerCase().replaceAll(" ", "-");
  const isMobile = useMediaQuery({ maxWidth: 750 });

  const browseFiles = () => {
    document.getElementById(uploadId)?.click();
  };

  const handleUploadClick = () => {
    if (onButtonClick) {
      onButtonClick();
      return;
    }
    browseFiles();
  };

  const onFileSelected = (event: React.FormEvent<HTMLInputElement>) => {
    handleFileSelected(event.currentTarget.files!, {
      allowedFileTypes,
      documentType,
      onError,
      onUploadedNewFiles,
      setIsDirty,
    });
  };

  return (
    <div>
      <FlexDescriptionButtonDiv isMobile={isMobile}>
        <InfoTextContainer>
          {description && <Typography>{description}</Typography>}
        </InfoTextContainer>
        <ButtonGroup>
          <Button
            color={buttonColor ?? "primary"}
            disabled={disabled || documentLoading}
            size={buttonSize}
            onClick={handleUploadClick}
            data-testid={`checkout-upload-documents-step-upload-${uploadId}`}
            iconRight={buttonIcon}
          >
            {documentLoading ? (
              <LoadingIndicator
                color={loadingColor}
                showLoadingMessage={false}
              />
            ) : (
              buttonText
            )}
          </Button>
          {getBillOnline && (
            <Button
              color={"primary"}
              disabled={disabled || documentLoading || !!intervalDataStatus}
              size={buttonSize}
              onClick={getBillOnline}
              data-testid={`checkout-upload-documents-step-get-bill-online-${uploadId}`}
            >
              Get Bill Online
            </Button>
          )}
        </ButtonGroup>
        <InputNoDisplay
          disabled={disabled || documentLoading}
          type="file"
          accept={allowedFileTypes.join(",")}
          onChange={onFileSelected}
          id={uploadId}
          data-testid={`file-upload-input-${uploadId}`}
          {...(acceptMultipleFiles && { multiple: true })}
        />
      </FlexDescriptionButtonDiv>
      <br />
      <FlexCenterDiv>
        {uploadedFiles.map((file: any, index: number) => (
          <AttachmentActionButton
            key={`${file.documentName}${index}`}
            disabled={disabled || documentLoading}
            file={file}
            allowPreview={allowPreview}
            allowDownload={allowDownload}
            data-testid={`checkout-upload-documents-step-document-${file.documentName}${index}`}
          />
        ))}
        {uploadedNewFiles.map((file: any, index: number) => (
          <AttachmentActionButton
            key={`${file.documentName}${index}-new`}
            disabled={disabled || documentLoading}
            file={file}
            allowPreview={allowPreview}
            allowDownload={allowDownload}
            allowDelete
            deleteFile={() => {
              const updated = uploadedNewFiles.filter((_, i) => i !== index);
              onUploadedNewFiles(updated);
              setIsDirty(true);
            }}
            data-testid={`checkout-upload-documents-step-document-${file.documentName}${index}`}
          />
        ))}
      </FlexCenterDiv>
    </div>
  );
};

const FlexDescriptionButtonDiv = styled.div<{ isMobile?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: ${({ isMobile }) => (isMobile ? "flex-start" : "center")};
  ${({ isMobile }) =>
    isMobile &&
    `
    flex-direction: column;
    width: 100%;
    gap: 24px;

    > button {
      width: 100%;
    }
  `}
`;

const FlexCenterDiv = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 25px;
`;

const InfoTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
`;

const InputNoDisplay = styled.input`
  display: none;
`;

type ConfirmationModalProps = {
  title: string;
  body: ReactNode;
};

type UploadAttachmentsProps = {
  uploadedFiles: Attachment[] | SplatCheckoutDocument[];
  uploadedNewFiles: Attachment[];
  onUploadedNewFiles: Dispatch<SetStateAction<Attachment[]>>;
  onError?: (errors: FileUploadError[]) => void;
  description?: string;
  documentType: string;
  allowedFileTypes?: string[];
  allowDownload?: boolean;
  allowPreview?: boolean;
  acceptMultipleFiles?: boolean;
  buttonSize?: ButtonProps["size"];
  disabled?: boolean;
  setIsDirty?: (arg0: boolean) => void;
  documentLoading?: boolean;
  loadingColor?: LoadingIndicatorProps["color"];
  buttonText?: string;
  buttonIcon?: ReactNode;
  buttonColor?: ButtonProps["color"];
  onButtonClick?: () => void;
  getBillOnline?: () => void;
  intervalDataStatus?: string;
};

export type { UploadAttachmentsProps, FileUploadError, ConfirmationModalProps };
export {
  UploadAttachments,
  handleFileSelected,
  ErrorType,
  IMAGE_RESIZE_THRESHOLD_MB,
};
